import L from "../vendor/leaflet/leaflet.js"
import {pinFlag, zoneCss} from "./ar_zones.js"
import {closeRing, isDeleteKey, minVertices, openRing, removeVertexAt} from "./section_geometry.js"

const DESKTOP_MIN = 1024
const MIN_VERTICES = minVertices()
const BASEMAP_KEY = "moon-scan-redact-basemap"

function markerIcon(el) {
  return L.icon({
    iconUrl: el.dataset.iconUrl,
    iconRetinaUrl: el.dataset.iconRetinaUrl,
    shadowUrl: el.dataset.shadowUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    tooltipAnchor: [16, -28],
    shadowSize: [41, 41],
  })
}

function parseNumber(value) {
  const number = parseFloat(value)
  return Number.isFinite(number) ? number : null
}

function streetsLayer() {
  return L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: "&copy; OpenStreetMap contributors",
  })
}

function satelliteLayer() {
  return L.tileLayer(
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    {
      maxZoom: 19,
      attribution:
        "Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community",
    }
  )
}

function addTiles(map) {
  const streets = streetsLayer()
  const satellite = satelliteLayer()
  const saved = sessionStorage.getItem(BASEMAP_KEY)
  ;(saved === "satellite" ? satellite : streets).addTo(map)
  const control = L.control.layers(
    {Streets: streets, Satellite: satellite},
    null,
    {collapsed: false, position: "topright"}
  )
  control.addTo(map)
  const box = control.getContainer()
  if (box) box.id = "redact-basemap"
  map.on("baselayerchange", (event) => {
    sessionStorage.setItem(BASEMAP_KEY, event.name === "Satellite" ? "satellite" : "streets")
  })
}

function ring(layer, latlng, metres, color, fill, fillOpacity) {
  if (!(metres > 0)) return
  L.circle(latlng, {
    radius: metres,
    color,
    weight: 2,
    fillColor: fill,
    fillOpacity,
    interactive: false,
  }).addTo(layer)
}

function drawRadii(layer, latlng, radii) {
  layer.clearLayers()
  if (!latlng) return
  if (radii.remoteFinite && radii.remote > 0) {
    ring(layer, latlng, radii.remote, zoneCss("remote"), zoneCss("remote"), 0.03)
  }
  ring(layer, latlng, radii.beacon, zoneCss("beacon"), zoneCss("beacon"), 0.04)
  ring(layer, latlng, radii.detection, zoneCss("detection"), zoneCss("detection"), 0.08)
  ring(layer, latlng, radii.activation, zoneCss("activation"), zoneCss("activation"), 0.16)
  ring(layer, latlng, radii.exclusion, zoneCss("exclusion"), zoneCss("exclusion"), 0.12)
}

function readRadii(dataset) {
  return {
    exclusion: parseNumber(dataset.exclusionRadiusM),
    activation: parseNumber(dataset.activationRadiusM),
    detection: parseNumber(dataset.detectionRadiusM),
    beacon: parseNumber(dataset.beaconRadiusM),
    remote: parseNumber(dataset.remoteRadiusM),
    remoteFinite: pinFlag(dataset.remoteFinite),
  }
}

export const RedactMap = {
  mounted() {
    this.onResize = () => this.syncDesktopGate()
    this.onKeydown = (e) => this.handleDeleteKey(e)
    this.syncDesktopGate()
    window.addEventListener("resize", this.onResize)
    window.addEventListener("keydown", this.onKeydown)

    const lat = parseNumber(this.el.dataset.lat)
    const lng = parseNumber(this.el.dataset.lng)
    const center = lat !== null && lng !== null ? [lat, lng] : [37.9838, 23.7275]
    const radii = readRadii(this.el.dataset)

    this.map = L.map(this.el, {
      zoomControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: false,
      boxZoom: true,
      keyboard: true,
      attributionControl: true,
    })
    this.map.setView(center, 16)
    addTiles(this.map)

    this.polyLayer = L.layerGroup().addTo(this.map)
    this.radiiLayer = L.layerGroup().addTo(this.map)
    drawRadii(this.radiiLayer, center, radii)
    this.marker = L.marker(center, {
      icon: markerIcon(this.el),
      interactive: false,
      keyboard: false,
    }).addTo(this.map)

    this.layers = new Map()
    this.vertexGroup = L.layerGroup().addTo(this.map)
    this.midGroup = L.layerGroup().addTo(this.map)
    this.selectedUid = null
    this.hovered = null
    this.dragging = false

    this.handleEvent("redact_payload", ({polygons, selected_uid}) => {
      this.selectedUid = selected_uid
      this.draw(polygons || [])
    })
  },

  destroyed() {
    window.removeEventListener("resize", this.onResize)
    window.removeEventListener("keydown", this.onKeydown)
    if (this.map) this.map.remove()
  },

  syncDesktopGate() {
    const desktop = window.innerWidth >= DESKTOP_MIN
    const phone = document.getElementById("carver-desktop-only")
    const shell = document.getElementById("carver-desktop")
    if (phone) phone.hidden = desktop
    if (shell) shell.hidden = !desktop
    if (this.el) this.el.hidden = !desktop
    if (desktop && this.map) {
      requestAnimationFrame(() => this.map.invalidateSize())
    }
  },

  draw(polygons) {
    this.hovered = null
    this.polyLayer.clearLayers()
    this.layers.clear()
    this.vertexGroup.clearLayers()
    this.midGroup.clearLayers()

    for (const poly of polygons) {
      const ringCoords = ringOf(poly.polygon)
      if (ringCoords.length < MIN_VERTICES) continue
      const latlngs = openRing(ringCoords)
        .map((pair) => {
          const lng = Number(pair && pair[0])
          const lat = Number(pair && pair[1])
          return Number.isFinite(lat) && Number.isFinite(lng) ? L.latLng(lat, lng) : null
        })
        .filter(Boolean)
      const selected = poly.uid === this.selectedUid
      const polygon = L.polygon(latlngs, {
        color: poly.color || "#7f1d1d",
        weight: selected ? 3 : 1.5,
        fillOpacity: selected ? 0.28 : 0.16,
        interactive: true,
      }).addTo(this.polyLayer)

      polygon.on("click", (e) => {
        L.DomEvent.stop(e)
        this.pushEvent("select", {uid: poly.uid})
      })

      this.layers.set(poly.uid, polygon)
      if (selected) this.bindEditor(polygon, poly.uid)
    }
  },

  bindEditor(polygon, uid) {
    this.vertexGroup.clearLayers()
    this.midGroup.clearLayers()
    this.bindVertices(polygon, uid)
    this.bindMidpoints(polygon, uid)
  },

  bindVertices(polygon, uid) {
    this.vertexGroup.clearLayers()
    const open = openLatLngs(polygon)

    open.forEach((ll, i) => {
      const marker = L.marker(ll, {
        draggable: true,
        zIndexOffset: 700,
        title: "Drag to move · double-click, right-click, or Delete to remove",
        icon: L.divIcon({
          className: "carver-vertex",
          html: '<span class="carver-vertex-core"></span>',
          iconSize: [14, 14],
        }),
      }).addTo(this.vertexGroup)

      marker.on("click", (e) => L.DomEvent.stop(e))
      marker.on("mouseover", () => {
        this.hovered = {uid, index: i}
        const el = marker.getElement()
        if (el) el.classList.add("carver-vertex--hot")
      })
      marker.on("mouseout", () => {
        if (this.hovered && this.hovered.uid === uid && this.hovered.index === i) {
          this.hovered = null
        }
        const el = marker.getElement()
        if (el) el.classList.remove("carver-vertex--hot")
      })
      marker.on("dblclick", (e) => {
        L.DomEvent.stop(e)
        this.deleteVertex(polygon, uid, i)
      })
      marker.on("contextmenu", (e) => {
        L.DomEvent.stop(e)
        this.deleteVertex(polygon, uid, i)
      })
      marker.on("dragstart", () => {
        this.dragging = true
      })
      marker.on("drag", () => {
        const pts = openLatLngs(polygon)
        pts[i] = marker.getLatLng()
        polygon.setLatLngs([pts])
        this.bindMidpoints(polygon, uid)
      })
      marker.on("dragend", () => {
        this.dragging = false
        this.commit(polygon, uid)
      })
    })
  },

  bindMidpoints(polygon, uid) {
    this.midGroup.clearLayers()
    const open = openLatLngs(polygon)
    const n = open.length
    if (n < 2) return

    for (let i = 0; i < n; i++) {
      const a = open[i]
      const b = open[(i + 1) % n]
      const mid = L.latLng((a.lat + b.lat) / 2, (a.lng + b.lng) / 2)
      const insertAt = i + 1
      const marker = L.marker(mid, {
        draggable: false,
        zIndexOffset: 650,
        title: "Click to add a vertex on this edge",
        icon: L.divIcon({
          className: "carver-midpoint",
          html: '<span class="carver-midpoint-core"></span>',
          iconSize: [10, 10],
        }),
      }).addTo(this.midGroup)

      marker.on("click", (e) => {
        L.DomEvent.stop(e)
        this.insertVertex(polygon, uid, insertAt, marker.getLatLng())
      })
    }
  },

  insertVertex(polygon, uid, index, latlng) {
    const open = openLatLngs(polygon)
    open.splice(index, 0, latlng)
    polygon.setLatLngs([open])
    this.bindEditor(polygon, uid)
    this.commit(polygon, uid)
  },

  deleteVertex(polygon, uid, index) {
    this.hovered = null
    const open = openLatLngs(polygon)
    const next = removeVertexAt(open, index)
    if (next.length === open.length) return
    polygon.setLatLngs([next])
    this.bindEditor(polygon, uid)
    this.commit(polygon, uid)
  },

  commit(polygon, uid) {
    const coords = openLatLngs(polygon).map((ll) => [ll.lng, ll.lat])
    this.pushEvent("polygon_changed", {uid, coordinates: closeRing(coords)})
  },

  handleDeleteKey(e) {
    if (!isDeleteKey(e.key)) return
    const target = e.target
    if (
      target &&
      (target.isContentEditable || /^(input|textarea|select)$/i.test(target.tagName))
    ) {
      return
    }
    if (!this.hovered || this.dragging) return
    e.preventDefault()
    const {uid, index} = this.hovered
    this.hovered = null
    const polygon = this.layers.get(uid)
    if (!polygon) return
    this.deleteVertex(polygon, uid, index)
  },
}

function openLatLngs(polygon) {
  const ring = (polygon.getLatLngs()[0] || []).slice()
  if (ring.length >= 2 && ring[0].equals(ring[ring.length - 1])) return ring.slice(0, -1)
  return ring
}

function ringOf(polygon) {
  const coords = polygon && polygon.coordinates
  if (!Array.isArray(coords) || !Array.isArray(coords[0])) return []
  return coords[0]
}
